import { http } from "./http";
export const importLabels: Record<string, string> = {
  firstName: "Prénom",
  lastName: "Nom",
  dateOfBirth: "Date de naissance",
  addressStreet: "Adresse",
  addressCity: "Ville",
  addressPostalCode: "Code postal",
  email: "Email",
  phoneNumber: "Téléphone",
  permit: "Permis",
  licenseCategory: "Catégorie de permis",
  vehicleType: "Famille de permis",
  formationType: "Type de formation",
  financingType: "Financement",
  trainingStartDate: "Inscription",
  externalRef: "NEPH",
  comment: "Notes",
  ordered: "Commandé",
  remainingConsumption: "Reste à conso",
  consumed: "Consommé",
  remainingPayment: "Reste à payer",
  paid: "Payé",
  dueToDate: "Dû à date",
  remainingHours: "Heures restantes",
};
export const importEuros = (cents: string) =>
  (Number(cents) / 100).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
  });
export interface ImportRow {
  row: number;
  action: string;
  profile: Record<string, string>;
  before: Record<string, string | null>;
  amounts: Record<string, string>;
  errors: string[];
  /** Anomalies signalées sans bloquer la reprise de l’élève. */
  warnings?: string[];
  /** Créance et heures de reprise enregistrées pour cet élève ; null si aucune. */
  opening?: { amountCents: string; hours: string } | null;
  cells: string[];
}
export interface ImportPreview {
  alreadyApplied?: {created:number;updated:number};
  batchId: string | null;
  sourceName: string;
  asOf: string;
  fileHash: string;
  headers: string[];
  /** Ligne de total du fichier, reconnue et laissée hors reprise. */
  totalRow?: number | null;
  /** Bureaux du fichier ; selected indique ceux retenus par cet aperçu. */
  offices?: { name: string; count: number; selected: boolean }[] | null;
  rows: ImportRow[];
}
export interface ImportInspection {
  sheets: string[];
  sheet: string;
  headers: string[];
  count: number;
  totalRow?: number | null;
  sample: { row: number; cells: string[] }[];
  mapping: Record<string, number>;
  /** Reprise validée dont la correspondance des colonnes est réappliquée à ce fichier. */
  /** `builtin` : format d’export connu, livré avec Permio. */
  profile?: { sourceName: string; updatedAt: string; builtin?: boolean } | null;
  fields: Record<string, string>;
}
export interface ImportBatch {
  id: string;
  sourceName: string;
  asOf: string;
  fileName: string;
  appliedAt: string;
  result: { created: number; updated: number };
  revertedAt: string | null;
  reversal: { archived: number; restored: number; kept: number } | null;
  revertible: boolean;
}
export interface ImportReversalRow {
  row: number;
  candidateId: string;
  name: string;
  action: "archive" | "restore";
  changes: { field: string; label: string; from: unknown; to: unknown }[];
  blocked: string | null;
}
export interface ImportReversal {
  rows: ImportReversalRow[];
  /** Empreinte de l’aperçu, renvoyée pour confirmer l’annulation. */
  key: string;
}
/** Import antérieur à la créance de reprise dont des soldes ne sont pas comptés dans les fiches. */
export interface PendingOpenings {
  batchId: string;
  count: number;
  /** Total des soldes à compter, en centimes (négatif : crédit des élèves). */
  amountCents: string;
}
export interface OpeningsPreview {
  batchId: string;
  sourceName: string;
  asOf: string;
  fileName: string;
  /** Fiches qui recevraient leur créance de reprise. */
  rows: { candidateId: string; name: string; row: number; amountCents: string; hours: string }[];
  /** Fiches laissées, avec la raison. */
  skipped: { candidateId: string; name: string; row: number; reason: string }[];
  totalCents: string;
  /** Empreinte de l’aperçu, renvoyée pour confirmer. */
  key: string;
}
export interface ImportRecord {
  id: string;
  batchId: string;
  sourceName: string;
  asOf: string;
  recordedAt: string;
  fileName: string;
  fileHash: string;
  snapshot: {
    headers: string[];
    cells: string[];
    amounts: Record<string, string>;
    /**
     * Créance de reprise décidée à l’import (`null` : aucune) ; absente des imports antérieurs au
     * 18/10/2026, dont le reste à payer n’était gardé que pour information.
     */
    opening?: { amountCents: string; hours: string } | null;
  };
}
export const candidateImportsApi = {
  inspect: async (data: FormData) =>
    (
      await http.post<ImportInspection>("/v1/candidate-imports/inspect", data, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data,
  preview: async (data: FormData) =>
    (
      await http.post<ImportPreview>("/v1/candidate-imports/preview", data, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data,
  apply: async (id: string) =>
    (
      await http.post<{ created: number; updated: number; batchId: string }>(
        `/v1/candidate-imports/${id}/apply`,
      )
    ).data,
  /** Seuls les titres de colonnes sont transmis à Permio AI. */
  suggestColumns: async (headers: string[]) =>
    (
      await http.post<{ mapping: Record<string, number> }>(
        "/v1/candidate-imports/suggest-columns",
        { headers },
      )
    ).data,
  extractPdf: async (data: FormData) =>
    (
      await http.post<{
        extractionId: string;
        fileName: string;
        csv: string;
        count: number;
        notes: string[];
      }>("/v1/candidate-imports/extract-pdf", data, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 200000,
      })
    ).data,
  batches: async () =>
    (await http.get<ImportBatch[]>("/v1/candidate-imports")).data,
  reversal: async (id: string) =>
    (await http.get<ImportReversal>(`/v1/candidate-imports/${id}/reversal`))
      .data,
  revert: async (id: string, key: string) =>
    (
      await http.post<{
        archived: number;
        restored: number;
        kept: number;
        batchId: string;
      }>(`/v1/candidate-imports/${id}/revert`, { key })
    ).data,
  pendingOpenings: async () =>
    (await http.get<PendingOpenings[]>("/v1/candidate-imports/openings")).data,
  openingsPreview: async (id: string) =>
    (await http.get<OpeningsPreview>(`/v1/candidate-imports/${id}/openings`))
      .data,
  countOpenings: async (id: string, key: string) =>
    (
      await http.post<{ counted: number; amountCents: string; batchId: string }>(
        `/v1/candidate-imports/${id}/openings`,
        { key },
      )
    ).data,
  history: async (id: string) =>
    (await http.get<ImportRecord[]>(`/v1/candidate-imports/candidates/${id}`))
      .data,
  evidence: async (id: string) =>
    (
      await http.get<Blob>(`/v1/candidate-imports/${id}/evidence`, {
        responseType: "blob",
      })
    ).data,
};
/** Modèle Permio : titres reconnus sans correspondance, une ligne d’exemple. */
export function importTemplateCsv() {
  const titles = [
    "Prénom", "Nom", "Date de naissance", "Email", "Téléphone", "Adresse", "Code postal", "Ville",
    "Permis", "NEPH", "Inscription", "Financeur", "Bureau", "Notes",
    "Commandé", "Payé", "Reste à payer", "Heures restantes",
  ];
  const example = [
    "Zoé", "Martin", "27/05/2005", "zoe.martin@exemple.fr", "0612345678", "12 rue des Lilas", "38000", "Grenoble",
    "B", "", "03/02/2026", "CPF", "", "",
    "1500,00", "500,00", "1000,00", "12h30",
  ];
  const line = (cells: string[]) => cells.map((c) => `"${c.replace(/"/g, '""')}"`).join(";");
  return "\ufeff" + [line(titles), line(example)].join("\r\n") + "\r\n";
}
export function importError(error: unknown) {
  const message = (
    error as { response?: { data?: { message?: string | string[] } } }
  )?.response?.data?.message;
  return Array.isArray(message)
    ? message.join(" · ")
    : message ||
        "Opération interrompue. Vous pouvez réessayer sans dupliquer la reprise.";
}
